import { ChangeDetectionStrategy, Component, computed, input, output } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MAT_DATE_LOCALE } from '@angular/material/core';
import { MatDatepicker, MatDatepickerModule } from '@angular/material/datepicker';
import { DATE_PICKER_FORMATS, DateRangeAdapter } from '@app/app/shared/utils/date-picker.utils';
import { LucideCalendar } from '@lucide/angular';
import { TranslocoModule } from '@ngneat/transloco';
import * as moment from 'moment';
import { IconComponent } from '../icon/icon.component';

/**
 * Date field with the V3 calendar popup. Values go in and out as `YYYY-MM-DD` strings, the same contract the
 * old native `<input type="date">` had, so callers did not change.
 */
@Component({
  selector: 'ptl-date-input',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [MatDatepickerModule, TranslocoModule, IconComponent],
  providers: [
    { provide: DateAdapter, useClass: DateRangeAdapter, deps: [MAT_DATE_LOCALE] },
    { provide: MAT_DATE_FORMATS, useValue: DATE_PICKER_FORMATS },
  ],
  templateUrl: './date-input.component.html',
  styleUrls: ['./date-input.component.scss'],
})
export class DateInputComponent {
  value = input('');
  disabled = input(false);
  min = input<string | null>(null);
  max = input<string | null>(null);
  valueChange = output<string>();

  selectedDate = computed(() => this.toDate(this.value()));
  minDate = computed(() => this.toDate(this.min()));
  maxDate = computed(() => this.toDate(this.max()));

  protected readonly LucideCalendar = LucideCalendar;

  openCalendar(picker: MatDatepicker<Date>): void {
    if (this.disabled()) {
      return;
    }
    picker.open();
  }

  onDateChange(date: Date | null): void {
    if (date) {
      this.valueChange.emit(moment(date).format('YYYY-MM-DD'));
    } else {
      this.valueChange.emit('');
    }
  }

  private toDate(value: string | null): Date | null {
    if (!value) {
      return null;
    }
    const [year, month, day] = value.split('-').map(Number);
    if (!year || !month || !day) {
      return null;
    }
    return new Date(year, month - 1, day);
  }
}
